import { useState, useEffect, useCallback } from 'react'
import type { PartyBuyingPreferenceInput } from '@adc/sdk/types'
import {
  fetchBuyingPreference,
  saveBuyingPreference,
} from '../providers/userInsights'
import type { TypedBuyingPreference } from '../providers/userInsights'

interface UseBuyingPreferenceResult {
  preference: TypedBuyingPreference | null
  isLoading: boolean
  isSaving: boolean
  error: Error | null
  savePreference: (input: PartyBuyingPreferenceInput) => Promise<void>
}

export function useBuyingPreference(
  partyId: string | null
): UseBuyingPreferenceResult {
  const [preference, setPreference] = useState<TypedBuyingPreference | null>(
    null
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!partyId) {
      setPreference(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    fetchBuyingPreference(partyId)
      .then(data => {
        if (!cancelled) {
          setPreference(data)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [partyId])

  const savePreference = useCallback(
    async (input: PartyBuyingPreferenceInput): Promise<void> => {
      if (!partyId) return

      setIsSaving(true)
      setError(null)

      try {
        const updated = await saveBuyingPreference(partyId, input)
        setPreference(updated)
      } catch (err) {
        const saveError = err instanceof Error ? err : new Error(String(err))
        setError(saveError)
        throw saveError
      } finally {
        setIsSaving(false)
      }
    },
    [partyId]
  )

  return { preference, isLoading, isSaving, error, savePreference }
}
